/**
 * Shared constants for the Reluna Copilot Agent Constructor feature.
 * The shell owns these so the four views render against one source of truth:
 * the tab set, the task-cycle ordering (doc 09 §3), the field-class color map
 * (doc 09 §5), and the constructor form option lists (doc 09 §2 agent contract).
 *
 * NOTE (phase 1): there is no static work-item catalog here anymore. Work items
 * are discovered live via GET /agents/:id/work-items (they are derived from real
 * platform transactions, ids like `rb-txn-<uuid>`) — see CopilotState.loadWorkItems.
 */

import {
  AgentStakesDTO,
  AgentTriggerDTO,
  AlertRuleKindDTO,
  AlertSeverityDTO,
  AlertWindowDTO,
  AutonomyLevelDTO,
  BudgetDTO,
  ControlPlaneCatalogDTO,
  FieldClassDTO,
  MinEncryptionDTO,
  ModelClassDTO,
  ReadinessSnapshotDTO,
  RecommendationCategoryDTO,
  RecommendationSeverityDTO,
  RecommendationStatusDTO,
  ReconVerdictDTO,
  ReportKindDTO,
  RulebookRuleDTO,
  TaskCycleStateDTO,
  WriteActionDTO,
} from '@dto/gateway-secured/models';
import { Authorities } from '@shared/constants/authorities';
import { Authority } from '@shared/types/authority';
import {
  AgentModuleOption,
  BudgetPresetOption,
  ContractFormOption,
  CopilotNavGroup,
  CopilotNavGroupKey,
  CopilotPollResource,
  CopilotPollView,
  CopilotTab,
  DescribedContractFormOption,
  FieldClassColorKey,
  ReadinessFeedRow,
  TabKey,
  TimelineStep,
} from './copilot.types';

/** Default tenant the shell selects on load (doc 09 §1; the entitled lane). */
export const DEFAULT_TENANT_ID = 'tenant-a';

/** The ten built-in agent ids wired in the runtime. Agent ids are an OPEN set:
 * a tenant admin authors custom agents at runtime via POST /agents/create. */
export const BUILT_IN_AGENT_IDS = {
  recon: 'recon-exception',
  nlAnalytics: 'nl-analytics',
  txnCompliance: 'transaction-compliance',
  portfolioWatch: 'portfolio-watch',
  priceAlert: 'price-alert',
  newsWatch: 'news-watch',
  docExtraction: 'document-extraction',
  cioDigest: 'cio-digest',
  investmentCommittee: 'investment-committee',
  strategyMonitor: 'strategy-monitor',
} as const;

/** The recon-exception agent id wired in the runtime (doc 09 §10). */
export const RECON_AGENT_ID = BUILT_IN_AGENT_IDS.recon;

/** The transaction-compliance agent id wired in the runtime (doc 09 §13). */
export const TXN_COMPLIANCE_AGENT_ID = BUILT_IN_AGENT_IDS.txnCompliance;

/**
 * The cio-digest agent id wired in the runtime (CIO fleet). The Reports tab's
 * digest empty state offers a "run it now" affordance for it — a work-item-less
 * launch (POST /runs without breakId; the runtime's singleton 'digest' item).
 */
export const CIO_DIGEST_AGENT_ID = BUILT_IN_AGENT_IDS.cioDigest;

/**
 * The investment-committee agent id wired in the runtime (CIO wave 2). A parked
 * run of THIS agent renders the committee-shaped approval card (minutes title +
 * proposals + debate verdict) instead of the recon-shaped one, and the
 * Governance Memory panel derives its view from this agent's latest run.
 */
export const INVESTMENT_COMMITTEE_AGENT_ID = BUILT_IN_AGENT_IDS.investmentCommittee;

/**
 * The remaining code agents whose parked drafts reach the approval queue. Each
 * holds its OWN result shape, so the queue picks the card by agent id (see
 * approvals/approval-preview.util.ts) instead of rendering recon fields for
 * everything that is not the committee.
 */
export const NL_ANALYTICS_AGENT_ID = BUILT_IN_AGENT_IDS.nlAnalytics;
export const PORTFOLIO_WATCH_AGENT_ID = BUILT_IN_AGENT_IDS.portfolioWatch;
export const PRICE_ALERT_AGENT_ID = BUILT_IN_AGENT_IDS.priceAlert;

/** The news-watch agent id wired in the runtime (CIO fleet). */
export const NEWS_WATCH_AGENT_ID = BUILT_IN_AGENT_IDS.newsWatch;

/** The document-extraction agent id wired in the runtime. */
export const DOC_EXTRACTION_AGENT_ID = BUILT_IN_AGENT_IDS.docExtraction;

/** The strategy-monitor agent id wired in the runtime (CIO fleet). */
export const STRATEGY_MONITOR_AGENT_ID = BUILT_IN_AGENT_IDS.strategyMonitor;

/**
 * Constructor module value -> the wired runtime agent id it provisions (doc 09
 * §2/§13). Only the two agents that exist as CODE in the runtime are mappable;
 * the other modules are catalog presets without a wired agent yet, so the
 * constructor's "create" reports them as preview-only (agent = code, doc 09 §2).
 */
export const MODULE_AGENT_ID: Readonly<Record<string, string>> = {
  reconciliation: RECON_AGENT_ID,
  'txn-compliance': TXN_COMPLIANCE_AGENT_ID,
};

/**
 * The recon verdicts a proposal can assert — the runtime's ReconVerdictDTO
 * union, and EXACTLY the four `pages.copilot.runViewer.verdict.*` keys this
 * build ships wording for.
 *
 * Declared HERE, once, because two surfaces answer the same question: the
 * Approvals card (isKnownVerdict) and the run viewer (which must decide whether
 * a run's `output` is a recon proposal at all). They used to hold private copies;
 * the run viewer's copy did not exist, so it cast EVERY run output to
 * ReconResultDTO and rendered `pages.copilot.runViewer.verdict.undefined` plus a
 * false "Reconciled: No" over news-watch / price-alert / portfolio-watch runs.
 */
export const RECON_VERDICTS: ReadonlySet<string> = new Set<string>([
  'matched',
  'genuine-break',
  'escalate',
  'rejected',
]);

/** True when `verdict` is one of the four recon verdicts this build can name. */
export function isReconVerdict(verdict: unknown): verdict is ReconVerdictDTO {
  return typeof verdict === 'string' && RECON_VERDICTS.has(verdict);
}

/**
 * Does a run's committed `output` actually carry the RECON proposal shape?
 *
 * Structural, not nominal: the runtime types `output` as an open payload and
 * every fleet agent puts its OWN shape there (news-watch's `{ assessmentSource,
 * universeTerms, items, skipped }`, price-alert's, …). Only a payload that
 * declares a known recon verdict AND the boolean `reconciled` that verdict is
 * about may be rendered as a reconciliation; anything else renders as itself.
 */
export function isReconResultPayload(output: unknown): boolean {
  if (!output || typeof output !== 'object') {
    return false;
  }

  const candidate = output as { verdict?: unknown; reconciled?: unknown };

  return isReconVerdict(candidate.verdict) && typeof candidate.reconciled === 'boolean';
}

/**
 * Polling interval (ms) for an in-flight run. This is the FALLBACK path now: the
 * live run state comes from the GET /runs/stream SSE (night build) and the poll
 * only runs when the stream errors/disconnects (see CopilotState). Kept
 * deliberately — do not delete — so a stream outage degrades to the prior poll.
 */
export const RUN_POLL_INTERVAL_MS = 1500;

/**
 * Error budget for the run poll loop: stop polling after this many consecutive
 * failed/empty getRun responses so a persistently failing run does not poll forever.
 */
export const RUN_POLL_MAX_FAILURES = 5;

export const RUN_POLL_REQUEST_TIMEOUT_MS = 10_000;

export const RUN_POLL_BACKOFF_FACTOR = 2;

export const RUN_POLL_BACKOFF_MAX_MS = 30_000;

// Must stay below the runtime's 5-minute removed-log retention.
export const RUN_POLL_DELTA_MAX_GAP_MS = 4 * 60_000;

export function nextPollBackoffDelayMs(priorDelayMs: number): number {
  const capped = Math.min(priorDelayMs * RUN_POLL_BACKOFF_FACTOR, RUN_POLL_BACKOFF_MAX_MS);
  const half = capped / 2;

  return half + Math.random() * half;
}

export const COPILOT_POLL_GATE_VIEWS: Readonly<
  Record<CopilotPollResource, ReadonlySet<CopilotPollView>>
> = {
  [CopilotPollResource.Runs]: new Set([
    CopilotPollView.Runs,
    CopilotPollView.Approvals,
    CopilotPollView.Governance,
  ]),
  [CopilotPollResource.Approvals]: new Set([CopilotPollView.Approvals]),
};

export const COPILOT_POLL_VIEW_BY_TAB: Partial<Record<TabKey, CopilotPollView>> = {
  // Overview leads with the drafts awaiting approval, so it keeps the approval
  // queue fresh exactly like the Approvals view does (the poll still only runs
  // for a user whose run-data read gate is open — COPILOT_POLL_GATE_VIEWS +
  // CopilotState.canReadRunData decide that, not this map).
  overview: CopilotPollView.Approvals,
  runs: CopilotPollView.Runs,
  approvals: CopilotPollView.Approvals,
  governance: CopilotPollView.Governance,
};

/**
 * Delay (ms) before the live run stream reconnects after it errors/ends. While
 * disconnected the state falls back to the interval poll; on a clean reconnect
 * the fresh 'snapshot' frame re-syncs runs + approvals and the poll stands down.
 */
export const RUN_STREAM_RECONNECT_MS = 3000;

/**
 * Page size for the governance audit + egress logs (doc 09 §7). The endpoints
 * return the FULL array today, so the governance view sorts newest-first and
 * renders only this many rows, revealing more in page-sized steps via "Load
 * more". Keeps the DOM bounded for tenants with thousands of records.
 */
export const AUDIT_PAGE_SIZE = 50;

/**
 * The approve authorities the runtime enforces on the approval decision
 * (POST /runs/:id/approve — one per wired lane: recon, transaction-compliance,
 * CIO). Declared here (above COPILOT_TABS) because the Approvals tab's entry
 * authorities are exactly this set; REVIEWER_APPROVE_AUTHORITIES (the reviewer
 * picker's no-catalog fallback) re-exports it so the two can never drift.
 */
export const COPILOT_APPROVE_AUTHORITIES: readonly Authority[] = Object.freeze([
  Authorities.AiAgentReconApprove,
  Authorities.AiAgentTxnComplianceApprove,
  Authorities.AiAgentCioApprove,
]);

/**
 * Authority domain accepted as a custom agent's execution identity. ADMIN is
 * the only migration/fleet authority that is not a contract identity.
 */
export function agentContractAuthorityValues(
  catalog?: ControlPlaneCatalogDTO | null,
): readonly Authority[] {
  const values = catalog
    ? catalog.authorities
        .filter((authority) => authority.family === 'agent')
        .map((authority) => authority.id)
    : Object.values(Authorities).filter((authority) => authority.startsWith('AI_AGENT_'));

  return Object.freeze(
    values.filter((authority) => authority !== Authorities.AiAgentAdmin) as Authority[],
  );
}

/** Parked authority constants remain generated, but are not in the runtime SKU catalog. */
const PARKED_AGENT_VIEW_AUTHORITIES: readonly Authority[] = Object.freeze([
  Authorities.AiAgentDocextractView,
]);

/**
 * VIEW authorities a custom contract may select. Runtime validates these as a
 * registered `AI_AGENT_*_VIEW`; parked constants must not leak through the
 * no-catalog fallback.
 */
export function assignableAgentViewAuthorityValues(
  catalog?: ControlPlaneCatalogDTO | null,
): readonly Authority[] {
  return Object.freeze(
    agentContractAuthorityValues(catalog).filter(
      (authority) =>
        authority.endsWith('_VIEW') && !PARKED_AGENT_VIEW_AUTHORITIES.includes(authority),
    ) as Authority[],
  );
}

/**
 * Actual artifact-read authorities wired by runtime 0ace2b1. NL analytics is
 * intentionally non-suffixed and therefore cannot be derived from a naming
 * convention. Custom contracts add the assignable VIEW domain above.
 */
const BUILT_IN_ARTIFACT_READ_AUTHORITIES: readonly Authority[] = Object.freeze([
  Authorities.AiAgentReconView,
  Authorities.AiAgentTxnComplianceView,
  Authorities.AiAgentNlAnalytics,
  Authorities.AiAgentAnomalyView,
  Authorities.AiAgentReportView,
  Authorities.AiAgentCioView,
  Authorities.AiAgentMandateView,
]);

export function agentArtifactReadAuthorityValues(
  catalog?: ControlPlaneCatalogDTO | null,
): readonly Authority[] {
  const registered = new Set(agentContractAuthorityValues(catalog));

  return Object.freeze(
    Array.from(
      new Set([
        ...BUILT_IN_ARTIFACT_READ_AUTHORITIES.filter((authority) => registered.has(authority)),
        ...assignableAgentViewAuthorityValues(catalog),
      ]),
    ),
  );
}

export const COPILOT_RUN_READ_AUTHORITIES: readonly Authority[] =
  agentArtifactReadAuthorityValues();

/**
 * Who may open the Overview view (.project#1877) — the explicit union of the
 * gates of the sections it summarizes:
 *  - drafts awaiting approval -> the Approvals read gate (COPILOT_RUN_READ_AUTHORITIES),
 *  - active findings          -> AI_AGENT_CIO_VIEW,
 *  - blocked agents           -> the agent list's read side (AI_AGENT_RECON_VIEW / AI_AGENT_ADMIN),
 *  - data status + recent actions (GET /readiness, GET /audit) -> AI_AGENT_ADMIN.
 *
 * AI_AGENT_CIO_VIEW, AI_AGENT_ALERT_VIEW and AI_AGENT_RECON_VIEW are run-read
 * authorities today as well (so each of them already shows the approvals card);
 * they are listed explicitly so the gate does not hinge on the catalog-derived
 * run-read list keeping them.
 *
 * AI_AGENT_REPORT_VIEW is deliberately EXCLUDED even though it is one of the
 * run-read authorities: a Report-only user must land on Reports-side views, not
 * on a summary page whose every section is empty or hidden for them. AI_ASSISTANT_ASK
 * is not here either (Ask-only lands on Chat).
 *
 * Built from primitives, never from COPILOT_ENTRY_AUTHORITIES (which is derived
 * FROM the tabs, so that would be circular). Every element is already a gate of
 * another tab, so the entry union is unchanged by adding Overview — asserted in
 * copilot-overview-gate.spec.ts. Like the entry union, an empty result throws at
 * load: an empty one_of list is "no gate" (UserState.hasAuthorities([]) === true),
 * which would show Overview to everybody.
 */
function deriveCopilotOverviewAuthorities(runRead: readonly Authority[]): readonly Authority[] {
  const derived = Array.from(
    new Set<Authority>([
      ...runRead.filter((authority) => authority !== Authorities.AiAgentReportView),
      Authorities.AiAgentCioView,
      Authorities.AiAgentAlertView,
      Authorities.AiAgentReconView,
      Authorities.AiAgentAdmin,
    ]),
  );

  if (derived.length === 0) {
    throw new Error(
      'COPILOT_OVERVIEW_AUTHORITIES is empty: an empty one_of list does NOT deny, ' +
        'it would show the Overview view to every user — refusing to build.',
    );
  }

  return Object.freeze(derived);
}

export const COPILOT_OVERVIEW_AUTHORITIES: readonly Authority[] = deriveCopilotOverviewAuthorities(
  COPILOT_RUN_READ_AUTHORITIES,
);

/**
 * S3b (review #5.2) — RUNTIME immutability for the tab list and every authority
 * array hanging off it. `readonly` is a TYPE-LEVEL annotation only: it is erased
 * at compile time, so `(COPILOT_TABS as CopilotTab[])[0].entryAuthorities` /
 * `(COPILOT_ENTRY_AUTHORITIES as Authority[]).length = 0` are ordinary writes at
 * runtime. That matters here specifically because emptying an authority list does
 * NOT deny — `UserState.hasAuthorities([])` returns TRUE and
 * SubscriptionCanMatchGuard only gates `if (authorities?.length)` — so a mutation
 * that empties the entry union AFTER module load would make /copilot PUBLIC, the
 * exact door FE2's load-time throw closes only for load time.
 *
 * Freezing is done IN PLACE, never on a copy: the Approvals tab's
 * `entryAuthorities` IS `COPILOT_APPROVE_AUTHORITIES` (the same object, so the two
 * can never drift — asserted by identity in copilot-entry-authorities-e8.spec.ts),
 * and copying would silently break that. Each tab OBJECT is frozen too, so a tab's
 * `authority` cannot be swapped/removed after load either (that would re-gate the
 * shell's per-tab visibility, which is recomputed on every CopilotComponent
 * construction, i.e. on every navigation).
 */
function freezeCopilotTabs(tabs: readonly CopilotTab[]): readonly CopilotTab[] {
  for (const tab of tabs) {
    if (tab.viewAuthorities) {
      Object.freeze(tab.viewAuthorities);
    }
    if (tab.entryAuthorities) {
      Object.freeze(tab.entryAuthorities);
    }
    Object.freeze(tab);
  }

  return Object.freeze(tabs);
}

/**
 * The container sub-nav tabs (doc 09 §1 + CIO wave). The Ask tab is gated on
 * AI_ASSISTANT_ASK; the CIO tabs mirror the runtime's route gating exactly
 * (recommendations/digest = CIO_VIEW, reports = REPORT_VIEW, alerts =
 * ALERT_VIEW) so a user never opens a tab whose every call would 403.
 *
 * `viewAuthorities` gates multi-lane read projections; `entryAuthorities`
 * additionally records action surfaces inside a readable tab. Action rights
 * admit the route but never reveal Runs/Approvals by themselves.
 *
 * FROZEN at load (S3b, review #5.2): the array itself, every tab object, and
 * every nested `entryAuthorities` array — see freezeCopilotTabs above.
 *
 * ORDER is the sidebar order (.project#1877: Overview & Chat / Work / Manage —
 * see COPILOT_TAB_GROUP), so the shell's "land on the first visible tab" lands
 * on the first item the user actually sees in the sidebar.
 */
export const COPILOT_TABS: readonly CopilotTab[] = freezeCopilotTabs([
  {
    key: 'overview',
    labelKey: 'pages.copilot.nav.overview',
    icon: 'Widget_16',
    // A summary of the sections below; its gate is COPILOT_OVERVIEW_AUTHORITIES
    // (see there). Each section inside is gated again by its own source view.
    entryAuthorities: COPILOT_OVERVIEW_AUTHORITIES,
  },
  {
    key: 'ask',
    labelKey: 'pages.copilot.nav.chat',
    icon: 'MessageAlert_16',
    authority: Authorities.AiAssistantAsk,
  },
  {
    key: 'approvals',
    labelKey: 'pages.copilot.nav.approvals',
    icon: 'CheckOutline_16',
    viewAuthorities: COPILOT_RUN_READ_AUTHORITIES,
    // The approval decision is authorized by the lane's *_APPROVE authority.
    entryAuthorities: COPILOT_APPROVE_AUTHORITIES,
  },
  {
    key: 'recommendations',
    labelKey: 'pages.copilot.nav.findings',
    icon: 'Search_16',
    authority: Authorities.AiAgentCioView,
    // Dismiss requires AI_AGENT_CIO_APPROVE (copilot-recommendations.component.ts:89).
    entryAuthorities: [Authorities.AiAgentCioApprove],
  },
  {
    key: 'alerts',
    labelKey: 'pages.copilot.nav.alerts',
    icon: 'StatusAttention_16',
    authority: Authorities.AiAgentAlertView,
    // Rule authoring requires AI_AGENT_ALERT_MANAGE (copilot-alerts.component.ts:71
    // canManage) — a MANAGE-only operator holds no VIEW (no implication, E8b).
    entryAuthorities: [Authorities.AiAgentAlertManage],
  },
  {
    key: 'reports',
    labelKey: 'pages.copilot.nav.reports',
    icon: 'File_16',
    authority: Authorities.AiAgentReportView,
    // The digest empty state's "run it now" requires AI_AGENT_CIO_RUN
    // (copilot-reports.component.ts:99 holdsCioRun).
    entryAuthorities: [Authorities.AiAgentCioRun],
  },
  {
    key: 'registry',
    labelKey: 'pages.copilot.nav.agentList',
    icon: 'AI_16',
    // The agent registry reads the recon lane's catalog and offers "create agent"
    // to an admin (copilot-registry.component.ts canManageAgents = AI_AGENT_ADMIN).
    // AI_AGENT_RECON_VIEW is the recon lane's read right — the original /copilot
    // audience, kept so this change can never NARROW entry.
    entryAuthorities: [Authorities.AiAgentReconView, Authorities.AiAgentAdmin],
  },
  {
    key: 'runs',
    labelKey: 'pages.copilot.nav.runs',
    icon: 'Play_16',
    viewAuthorities: COPILOT_RUN_READ_AUTHORITIES,
    // Trigger Run offers every enabled agent whose RUN authority the user holds
    // (copilot-run-viewer.component.ts:89, derived live from GET /agents). The two
    // agents wired as CODE today are recon-exception (AI_AGENT_RECON_RUN) and
    // transaction-compliance (AI_AGENT_TXN_COMPLIANCE_RUN) — see MODULE_AGENT_ID.
    entryAuthorities: [Authorities.AiAgentReconRun, Authorities.AiAgentTxnComplianceRun],
  },
  {
    key: 'governance',
    labelKey: 'pages.copilot.nav.governance',
    icon: 'StatusSuccessCircleOutline_16',
    // Exactly the panel's own one_of set (copilot-governance.component.ts:201-208
    // canViewPolicies) plus canAdmin (:200).
    entryAuthorities: [
      Authorities.AiAgentAdmin,
      Authorities.AiAgentAlertView,
      Authorities.AiAgentAlertManage,
    ],
  },
  {
    key: 'settings',
    labelKey: 'pages.copilot.nav.settings',
    icon: 'Settings_16',
    // GET/PUT /runtime-settings are both AI_AGENT_ADMIN + tenant isolation — the
    // same gate /egress-policy and /post-governance/settings already use. The tab
    // is therefore hidden for a non-admin; if it is reached anyway (a stale
    // authority set), the pane renders the runtime's 403 as a permission state,
    // never as empty or default-looking values.
    authority: Authorities.AiAgentAdmin,
  },
]);

/**
 * Derives the authority set that admits ENTRY to /copilot from the tab list —
 * and REFUSES (throws) if that set comes out empty (FE2, see the invariant
 * below). Exported so a spec can drive it with a hostile tab list; the shipped
 * value is COPILOT_ENTRY_AUTHORITIES right underneath, computed at module load.
 *
 * The set is the de-duplicated UNION of each tab's effective visibility gate:
 * `authority`, else `viewAuthorities`, else `entryAuthorities`. Action rights
 * inside a separately readable tab never admit an empty shell. Consumed as the
 * one_of list by BOTH the route (app-routing.module.ts `data.authorities`, read
 * by CopilotCanMatchGuard + SubscriptionCanMatchGuard) and the navbar item
 * (navbar.component.ts) — that is the whole point: entry ≡ "holds ≥1 tab
 * authority", BY CONSTRUCTION.
 *
 * E8 (review #5): the route admitted a NARROWER set than the tabs required
 * (RECON_VIEW/RECON_RUN/RECON_APPROVE/ADMIN/ALERT_VIEW) and the navbar a narrower
 * one still (RECON_VIEW + ALERT_VIEW), so a user entitled to a TAB —
 * AI_AGENT_REPORT_VIEW-only, AI_ASSISTANT_ASK-only, AI_AGENT_CIO_VIEW-only —
 * could not OPEN the page at all (and never even saw the menu item). Deriving
 * entry from the tab set means adding a tab can never again lock its audience
 * out. Per-tab gating INSIDE the page is unchanged, and a user holding NONE of
 * these still cannot enter (the guards' one_of over this list is false).
 */
export function deriveCopilotEntryAuthorities(tabs: readonly CopilotTab[]): readonly Authority[] {
  const derived = Array.from(
    new Set(
      tabs.flatMap((tab) =>
        tab.authority
          ? [tab.authority]
          : tab.viewAuthorities?.length
            ? [...tab.viewAuthorities]
            : [...(tab.entryAuthorities ?? [])],
      ),
    ),
  );

  // FE2 (review #5.1) — HARD FAIL-CLOSED INVARIANT, evaluated at module load.
  //
  // An EMPTY authority list does not close the door, it REMOVES it:
  // SubscriptionCanMatchGuard only gates when `authorities?.length` is truthy
  // (subscription.guard.ts) and UserState.hasAuthorities([]) returns TRUE
  // (user.state.ts) — so a route whose `data.authorities` is [] admits a user
  // holding nothing, i.e. /copilot would become PUBLIC to any authenticated
  // session. While the list was a hardcoded literal it could not become empty;
  // now that it is DERIVED, a future edit that strips `authority` /
  // `entryAuthorities` from every tab would silently do exactly that.
  // Throwing here turns that into a boot/build failure instead of a data leak.
  if (derived.length === 0) {
    throw new Error(
      'COPILOT_ENTRY_AUTHORITIES is empty: every COPILOT_TABS entry lost its authority/entryAuthorities. ' +
        'An empty one_of list disables the route + navbar gate entirely (it does NOT deny), ' +
        'which would make /copilot public — refusing to build.',
    );
  }

  // S3b (review #5.2): the throw above is a LOAD-TIME check only — it cannot stop a
  // later `(COPILOT_ENTRY_AUTHORITIES as Authority[]).length = 0`, which would empty
  // the one_of list the route + navbar read on EVERY navigation and thereby open
  // /copilot to any authenticated session (hasAuthorities([]) === TRUE). Freezing
  // makes that write a TypeError under the module's strict mode instead of a
  // silently open door: the invariant now holds for the process lifetime, not just
  // for the first millisecond of it.
  return Object.freeze(derived);
}

export const COPILOT_ENTRY_AUTHORITIES: readonly Authority[] =
  deriveCopilotEntryAuthorities(COPILOT_TABS);

/**
 * The sidebar group each view is listed under (.project#1877). A Record over
 * TabKey, so a new view that is not placed in a group fails to compile instead
 * of silently missing from the sidebar. Presentation only: grouping never gates
 * anything — visibility stays with COPILOT_TABS + copilotTabIsVisible.
 */
export const COPILOT_TAB_GROUP: Readonly<Record<TabKey, CopilotNavGroupKey>> = Object.freeze({
  overview: 'overviewChat',
  ask: 'overviewChat',
  approvals: 'work',
  recommendations: 'work',
  alerts: 'work',
  reports: 'work',
  registry: 'manage',
  runs: 'manage',
  governance: 'manage',
  settings: 'manage',
});

/** The sidebar groups in display order, with their i18n titles. */
export const COPILOT_NAV_GROUPS: readonly { key: CopilotNavGroupKey; labelKey: string }[] =
  Object.freeze([
    Object.freeze({ key: 'overviewChat', labelKey: 'pages.copilot.navGroup.overviewChat' }),
    Object.freeze({ key: 'work', labelKey: 'pages.copilot.navGroup.work' }),
    Object.freeze({ key: 'manage', labelKey: 'pages.copilot.navGroup.manage' }),
  ] as const);

/**
 * The reasons the verifier escalates a draft to a human (doc 09 §3 Verify ->
 * HumanApprove) that have a translated label (pages.copilot.approvals.reason.*).
 * Any other reason renders raw. Shared by the Approvals view and the Overview's
 * "drafts awaiting approval" card so the two can never translate differently.
 */
export const APPROVAL_ESCALATION_REASONS: ReadonlySet<string> = new Set([
  'low-confidence',
  'cross-model-disagreement',
  'high-stakes',
  'missing-provenance',
  'ungrounded-number',
]);

/** The four readiness feeds, in display order, with their i18n label keys. */
const READINESS_FEEDS: readonly {
  key: keyof ReadinessSnapshotDTO['feeds'];
  labelKey: string;
}[] = [
  { key: 'transactions', labelKey: 'pages.copilot.readiness.feed.transactions' },
  { key: 'positions', labelKey: 'pages.copilot.readiness.feed.positions' },
  { key: 'prices', labelKey: 'pages.copilot.readiness.feed.prices' },
  { key: 'strategies', labelKey: 'pages.copilot.readiness.feed.strategies' },
];

/**
 * Project the readiness snapshot's keyed feed map into an ordered display list
 * (the feeds object is not iterable). Pure — the shell's readiness strip and the
 * Overview's "Data status" card render the same rows.
 */
export function copilotReadinessFeedRows(readiness: ReadinessSnapshotDTO): ReadinessFeedRow[] {
  return READINESS_FEEDS.map((feed) => ({
    labelKey: feed.labelKey,
    state: readiness.feeds[feed.key].state,
  }));
}

/**
 * Groups the ALREADY-FILTERED visible tabs for the sidebar, keeping their order.
 * A group with no visible view is dropped — the sidebar never renders an empty
 * heading. Pure, so the template and the specs share one implementation.
 */
export function copilotNavGroups(tabs: readonly CopilotTab[]): CopilotNavGroup[] {
  return COPILOT_NAV_GROUPS.map((group) => ({
    key: group.key,
    labelKey: group.labelKey,
    tabs: tabs.filter((tab) => COPILOT_TAB_GROUP[tab.key] === group.key),
  })).filter((group) => group.tabs.length > 0);
}

/**
 * Sentinel picker value for "Default (first available)" (phase 1): the run is
 * POSTed WITHOUT `breakId` and the runtime falls back to the first available
 * work item for the tenant. Always offered, even when work-item discovery
 * errors or returns nothing.
 */
export const DEFAULT_WORK_ITEM_ID = '__default__';

/**
 * Timeout (ms) for the INITIAL runtime load (GET /tenants). The page used to
 * show skeletons forever when the runtime was unreachable; past this window
 * the state flips runtimeUnavailable$ so the shell renders the dismissible
 * "runtime unreachable" banner instead (CIO wave onboarding hints).
 */
export const INITIAL_LOAD_TIMEOUT_MS = 15000;

// NOTE (CIO wave): the old RUN_TRIGGER_AGENTS pair is GONE. The Trigger Run
// panel derives its agent choices LIVE from GET /agents — every agent that is
// enabled for the tenant and whose RUN authority the user holds is runnable
// (see CopilotRunViewerComponent.triggerAgents$). Work-item catalogs stay
// discovered per agent + tenant via GET /agents/:id/work-items.

/** GET /recommendations status filter options (the backend enum, fail-closed). */
export const RECOMMENDATION_STATUS_OPTIONS: readonly RecommendationStatusDTO[] = [
  'active',
  'superseded',
  'dismissed',
  'expired',
  'adopted-by-committee',
];

/** GET /recommendations category filter options (the backend enum, fail-closed). */
export const RECOMMENDATION_CATEGORY_OPTIONS: readonly RecommendationCategoryDTO[] = [
  'price-alert',
  'news',
  'strategy-drift',
  'portfolio-risk',
  'committee',
];

/** Recommendation / alert severities in display order (shared chip scale). */
export const RECOMMENDATION_SEVERITY_OPTIONS: readonly RecommendationSeverityDTO[] = [
  'info',
  'warn',
  'critical',
];

/** GET /reports kind filter options (the backend enum, fail-closed). */
export const REPORT_KIND_OPTIONS: readonly ReportKindDTO[] = [
  'cio-digest',
  'committee-minutes',
  'ad-hoc',
];

/** PUT /alert-rules detector kinds (backend domain/alert-rules.ts). */
export const ALERT_RULE_KIND_OPTIONS: readonly AlertRuleKindDTO[] = [
  'price-move',
  'concentration',
  'drift',
  'position-loss',
];

/** Alert severities (backend domain/alert-rules.ts). */
export const ALERT_SEVERITY_OPTIONS: readonly AlertSeverityDTO[] = ['info', 'warn', 'critical'];

/** Alert measurement windows (backend domain/alert-rules.ts). */
export const ALERT_WINDOW_OPTIONS: readonly AlertWindowDTO[] = ['1d', '7d'];

/**
 * The alert-rule id grammar PUT /alert-rules enforces (backend
 * ALERT_RULE_ID_RE): dedup-key + notification-line safe — no whitespace, no
 * '|', max 64 chars. Mirrored so the rule dialog never PUTs an id the runtime
 * would 400.
 */
export const ALERT_RULE_ID_RE = /^[A-Za-z0-9._:-]{1,64}$/;

/** Threshold bound PUT /alert-rules enforces (bps; 10000 = 100%). */
export const ALERT_THRESHOLD_BPS_MAX = 1_000_000;

/** Dismiss-reason bound POST /recommendations/:id/dismiss enforces. */
export const DISMISS_REASON_MAX_LENGTH = 500;

/**
 * The bounds PUT /rulebook enforces, mirrored from the runtime admission gate
 * (reluna-agent-runtime src/domain/rulebook.ts RULEBOOK_BOUNDS) so the editor
 * can flag an over-long code / clause / appliesTo list BEFORE the round trip.
 * The runtime remains the authority — these only pre-empt an obvious 400, they
 * never relax anything the server enforces.
 */
export const RULEBOOK_BOUNDS = {
  maxRules: 200,
  codeMaxChars: 64,
  textMaxChars: 2000,
  appliesToMaxEntries: 20,
  appliesToEntryMaxChars: 32,
  maxSettlementDaysMax: 365,
} as const;

/**
 * A STARTER TEMPLATE for a tenant that has no compliance rulebook yet.
 *
 * This is NOT the customer's policy and is never installed automatically: it is
 * loaded into the editor ONLY when an admin explicitly asks for it, it is not
 * saved until the admin saves it, and — like every authored version — it lands
 * as a DRAFT that has to be walked through test/approve/activate before it
 * governs anything. The UI labels it as a template for exactly that reason.
 *
 * The clauses are deliberately generic settlement/data-quality checks phrased as
 * house conventions to be edited, not as legal or regulatory advice. Each one
 * satisfies the runtime admission gate (unique code ≤ 64 chars, prose ≤ 2000
 * chars, enum severity, ≤ 20 appliesTo entries of ≤ 32 chars, maxSettlementDays
 * in [0, 365] — src/domain/rulebook.ts). `appliesTo` uses the platform's own
 * transaction-type spelling (TransactionDTO.TypeEnum: 'BUY' / 'SELL' / …); the
 * runtime folds case and separators before comparing (src/domain/txn-type.ts
 * txnTypeGovernedBy), so the clause matches real bookings.
 */
export const RULEBOOK_STARTER_TEMPLATE: readonly RulebookRuleDTO[] = [
  {
    code: 'TEMPLATE-SETTLE-EQUITY',
    text:
      'Equity purchases and sales must settle within the standard settlement window agreed with ' +
      'the custodian. Replace this text with the window your firm actually applies, and adjust ' +
      'maxSettlementDays to match it.',
    severity: 'warning',
    appliesTo: ['BUY', 'SELL'],
    maxSettlementDays: 2,
  },
  {
    code: 'TEMPLATE-SETTLE-CASH',
    text:
      'Cash movements must settle within the window agreed with the custodian. Replace this text ' +
      'with the window your firm actually applies, and adjust maxSettlementDays to match it.',
    severity: 'warning',
    appliesTo: ['CASH_TRANSFER', 'PAYMENT', 'WITHDRAWAL'],
    maxSettlementDays: 1,
  },
  {
    code: 'TEMPLATE-INSTRUMENT-RESOLVES',
    text:
      'Every security transaction must reference an instrument that resolves in the instrument ' +
      'master, and must carry a non-zero quantity. Edit or remove this clause if your firm ' +
      'handles unresolved instruments differently.',
    severity: 'critical',
    appliesTo: ['BUY', 'SELL'],
  },
  {
    code: 'TEMPLATE-REVIEW-LARGE',
    text:
      'Transactions that are unusually large for the mandate are reviewed by a second pair of ' +
      'eyes before they are booked. Replace this text with the threshold and the reviewer role ' +
      'your firm actually uses.',
    severity: 'info',
  },
];

/**
 * The canonical task-cycle ordering (doc 09 §3). 'Aborted' is the terminal
 * failure state and is rendered separately, not as a linear step.
 */
export const TASK_CYCLE_ORDER: readonly TaskCycleStateDTO[] = [
  'Perceive',
  'Plan',
  'Act',
  'Draft',
  'SelfCheck',
  'Verify',
  'HumanApprove',
  'Commit',
  'Audit',
  'Learn',
];

/** Timeline steps with their i18n label keys (doc 09 §3). */
export const TASK_CYCLE_TIMELINE: readonly TimelineStep[] = TASK_CYCLE_ORDER.map((state) => ({
  state,
  labelKey: `pages.copilot.runViewer.states.${state}`,
  hintKey: `pages.copilot.hint.state.${state}`,
}));

/**
 * Field-class -> theme color token (doc 09 §5 policy matrix). Views pass the
 * value to app-color() / a CSS var; keeping the keys here avoids per-view drift.
 */
export const FIELD_CLASS_COLOR: Readonly<Record<FieldClassColorKey, string>> = {
  public: 'success-color',
  internal: 'info-color',
  pii: 'warning-color',
  restricted: 'danger-color',
};

/** All field classes in display order (doc 09 §5). */
export const FIELD_CLASSES: readonly FieldClassDTO[] = ['public', 'internal', 'pii', 'restricted'];

// --- Constructor form option lists (doc 09 §2 agent contract) ----------------

/**
 * Budget bounds enforced by POST /agents/create (backend contract-draft.ts
 * BUDGET_BOUNDS). Create mode validates the inputs and drops presets outside
 * these caps; edit mode (wired code agents) is not bound by them.
 */
export const CREATE_BUDGET_BOUNDS = {
  maxSteps: { min: 1, max: 25 },
  maxTokens: { min: 1, max: 200000 },
  maxWallClockMs: { min: 1000, max: 120000 },
} as const;

/** True when a preset budget fits every POST /agents/create bound. */
export function budgetWithinCreateBounds(budget: BudgetDTO): boolean {
  return (Object.keys(CREATE_BUDGET_BOUNDS) as (keyof typeof CREATE_BUDGET_BOUNDS)[]).every(
    (field) =>
      budget[field] >= CREATE_BUDGET_BOUNDS[field].min &&
      budget[field] <= CREATE_BUDGET_BOUNDS[field].max,
  );
}

/** Mission length bounds enforced by POST /agents/create (phase 3 custom agents). */
export const MISSION_MIN_LENGTH = 20;
export const MISSION_MAX_LENGTH = 2000;

/** Output-schema-hint length bound enforced by POST /agents/create (phase 3). */
export const OUTPUT_SCHEMA_HINT_MAX_LENGTH = 2000;

/** Default minimum-encryption posture for a newly added data-source grant. */
export const DEFAULT_GRANT_ENCRYPTION: MinEncryptionDTO = 'encoded';

/**
 * Data sources a custom agent can be granted read access to (phase 3 + CIO
 * wave 2). The ids mirror the backend RetrievalQuery['kind'] union in
 * reluna-agent-runtime (transactions | positions | instruments | clients |
 * rulebook | mandates | recommendations | news | prices | strategies) — POST
 * /agents/create rejects grants for unknown source ids.
 */
export const DATA_SOURCE_OPTIONS: readonly DescribedContractFormOption[] = [
  {
    value: 'transactions',
    labelKey: 'pages.copilot.constructor.dataSource.transactions',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.transactions',
  },
  {
    value: 'positions',
    labelKey: 'pages.copilot.constructor.dataSource.positions',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.positions',
  },
  {
    value: 'instruments',
    labelKey: 'pages.copilot.constructor.dataSource.instruments',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.instruments',
  },
  {
    value: 'clients',
    labelKey: 'pages.copilot.constructor.dataSource.clients',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.clients',
  },
  {
    value: 'rulebook',
    labelKey: 'pages.copilot.constructor.dataSource.rulebook',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.rulebook',
  },
  {
    value: 'mandates',
    labelKey: 'pages.copilot.constructor.dataSource.mandates',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.mandates',
  },
  {
    value: 'recommendations',
    labelKey: 'pages.copilot.constructor.dataSource.recommendations',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.recommendations',
  },
  {
    value: 'news',
    labelKey: 'pages.copilot.constructor.dataSource.news',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.news',
  },
  {
    value: 'prices',
    labelKey: 'pages.copilot.constructor.dataSource.prices',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.prices',
  },
  {
    value: 'strategies',
    labelKey: 'pages.copilot.constructor.dataSource.strategies',
    descriptionKey: 'pages.copilot.constructor.desc.dataSource.strategies',
  },
];

/**
 * Minimum-encryption postures for a data-source grant (phase 3): what the agent
 * is allowed to see from that source at most — plaintext, tokenized, or nothing.
 */
export const MIN_ENCRYPTION_OPTIONS: readonly DescribedContractFormOption<MinEncryptionDTO>[] = [
  {
    value: 'decrypted',
    labelKey: 'pages.copilot.constructor.encryption.decrypted',
    descriptionKey: 'pages.copilot.constructor.desc.encryption.decrypted',
  },
  {
    value: 'encoded',
    labelKey: 'pages.copilot.constructor.encryption.encoded',
    descriptionKey: 'pages.copilot.constructor.desc.encryption.encoded',
  },
  {
    value: 'refuse',
    labelKey: 'pages.copilot.constructor.encryption.refuse',
    descriptionKey: 'pages.copilot.constructor.desc.encryption.refuse',
  },
];

/** Autonomy ceiling options (doc 09 §2; earned, defaults to draft-only). */
export const AUTONOMY_OPTIONS: readonly DescribedContractFormOption<AutonomyLevelDTO>[] = [
  {
    value: 'draft-only',
    labelKey: 'pages.copilot.constructor.autonomy.draft-only',
    descriptionKey: 'pages.copilot.constructor.desc.autonomy.draft-only',
  },
  {
    value: 'auto-with-review',
    labelKey: 'pages.copilot.constructor.autonomy.auto-with-review',
    descriptionKey: 'pages.copilot.constructor.desc.autonomy.auto-with-review',
  },
  {
    value: 'auto',
    labelKey: 'pages.copilot.constructor.autonomy.auto',
    descriptionKey: 'pages.copilot.constructor.desc.autonomy.auto',
  },
];

/**
 * The event-kind grammar the runtime enforces for `eventSource` on a
 * `trigger: 'event'` agent (night build reluna-agent-runtime/src/domain/events.ts
 * EVENT_KIND_RE): a lowercase dotted/hyphenated token. Mirrored here so the
 * constructor validates client-side and never POSTs an eventSource the create
 * would 400. Anchored (^…$) so the whole value must match.
 */
export const EVENT_KIND_RE = /^[a-z][a-z0-9]*([.-][a-z0-9]+)*$/;

/** Max length of an eventSource kind (night build EVENT_KIND_MAX_CHARS). */
export const EVENT_KIND_MAX_LENGTH = 64;

/**
 * The daily-schedule grammar the runtime enforces for a `trigger: 'scheduled'`
 * agent's `schedule` (night build contract-draft.ts SCHEDULE_RE): 'HH:MM' 24-hour
 * UTC (e.g. '06:30'), NOT cron. Mirrored so the constructor never POSTs a schedule
 * the create would 400.
 */
export const SCHEDULE_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Trigger options (doc 09 §2 trigger). */
export const TRIGGER_OPTIONS: readonly DescribedContractFormOption<AgentTriggerDTO>[] = [
  {
    value: 'on-demand',
    labelKey: 'pages.copilot.constructor.trigger.on-demand',
    descriptionKey: 'pages.copilot.constructor.desc.trigger.on-demand',
  },
  {
    value: 'scheduled',
    labelKey: 'pages.copilot.constructor.trigger.scheduled',
    descriptionKey: 'pages.copilot.constructor.desc.trigger.scheduled',
  },
  {
    value: 'event',
    labelKey: 'pages.copilot.constructor.trigger.event',
    descriptionKey: 'pages.copilot.constructor.desc.trigger.event',
  },
];

/** Stakes options (doc 09 §4.5/§4.6). */
export const STAKES_OPTIONS: readonly DescribedContractFormOption<AgentStakesDTO>[] = [
  {
    value: 'low',
    labelKey: 'pages.copilot.constructor.stakes.low',
    descriptionKey: 'pages.copilot.constructor.desc.stakes.low',
  },
  {
    value: 'high',
    labelKey: 'pages.copilot.constructor.stakes.high',
    descriptionKey: 'pages.copilot.constructor.desc.stakes.high',
  },
];

/**
 * Model-class options — the REAL backend cost/capability tier POST /agents/create
 * accepts ('cheap' | 'strong', reluna-agent-runtime/src/domain/provider.ts). This
 * is a TIER, not a provider lane: the local-vs-external lane is governed by the
 * data-source-grant encryption posture + the egress policy the dialog exposes
 * separately, so the old local/external-zdr/frontier options were both a 400 on
 * create and a false-privacy affordance ('local' never kept data local via this
 * field). Sending anything other than 'cheap'|'strong' 400s the create.
 */
export const MODEL_CLASS_OPTIONS: readonly DescribedContractFormOption<ModelClassDTO>[] = [
  {
    value: 'cheap',
    labelKey: 'pages.copilot.constructor.modelClass.cheap',
    descriptionKey: 'pages.copilot.constructor.desc.modelClass.cheap',
  },
  {
    value: 'strong',
    labelKey: 'pages.copilot.constructor.modelClass.strong',
    descriptionKey: 'pages.copilot.constructor.desc.modelClass.strong',
  },
];

/**
 * The FOUR memory layers the backend accepts (doc 09 §4.2; runtime
 * domain/contract-draft.ts MEMORY_LAYERS — an unknown name is a 400
 * "memoryScope.<read|write> names unknown memory layer(s)"). Read AND write are
 * chosen independently over this same list, because that is the contract's real
 * shape: `{ read: MemoryLayer[], write: MemoryLayer[] }`.
 *
 * This replaces the old four PRESETS ('none' | 'episodic' | 'semantic' |
 * 'both'), which could not express the contract: they always emitted
 * `write: []` — except 'both', which OMITTED the field so the server's default
 * landed instead, quietly granting `write: ['procedural']`. Two presets, two
 * different write postures, neither one stated; and 'working' / 'procedural'
 * were not offered at all.
 */
export const MEMORY_LAYER_OPTIONS: readonly DescribedContractFormOption[] = [
  {
    value: 'working',
    labelKey: 'pages.copilot.constructor.memoryLayer.working',
    descriptionKey: 'pages.copilot.constructor.desc.memoryLayer.working',
  },
  {
    value: 'episodic',
    labelKey: 'pages.copilot.constructor.memoryLayer.episodic',
    descriptionKey: 'pages.copilot.constructor.desc.memoryLayer.episodic',
  },
  {
    value: 'semantic',
    labelKey: 'pages.copilot.constructor.memoryLayer.semantic',
    descriptionKey: 'pages.copilot.constructor.desc.memoryLayer.semantic',
  },
  {
    value: 'procedural',
    labelKey: 'pages.copilot.constructor.memoryLayer.procedural',
    descriptionKey: 'pages.copilot.constructor.desc.memoryLayer.procedural',
  },
];

/**
 * The server-side memoryScope default (runtime contract-draft.ts
 * DEFAULT_MEMORY_SCOPE) — what lands when the field is OMITTED. Declared here so
 * the constructor can SEED it visibly and then always send the field explicitly:
 * an omitted memoryScope is the one shape whose write grant the author never
 * sees.
 */
export const DEFAULT_MEMORY_READ: readonly string[] = ['procedural', 'episodic'];
export const DEFAULT_MEMORY_WRITE: readonly string[] = ['procedural'];

/** Business-facing module presets; each maps to the technical layer/authority. */
export const CONSTRUCTOR_MODULE_OPTIONS: readonly AgentModuleOption[] = [
  {
    value: 'reconciliation',
    labelKey: 'pages.copilot.constructor.module.reconciliation',
    descriptionKey: 'pages.copilot.constructor.desc.module.reconciliation',
    layer: 1,
    authority: Authorities.AiAgentReconRun,
    defaultTrigger: 'event',
    defaultTools: ['book.retrieve', 'custodian.retrieve', 'book.match'],
    defaultMemoryRead: [...DEFAULT_MEMORY_READ],
    defaultMemoryWrite: [...DEFAULT_MEMORY_WRITE],
    defaultModelClass: 'cheap',
    defaultBudgetPreset: 'standard',
    defaultAutonomyLevel: 'auto-with-review',
    defaultStakes: 'high',
    defaultClientFacing: false,
    defaultCrossModel: true,
    defaultMission:
      'Investigate each custodian/book reconciliation break and draft a proposed tie-out or escalation with cited records.',
  },
  {
    value: 'anomaly',
    labelKey: 'pages.copilot.constructor.module.anomaly',
    descriptionKey: 'pages.copilot.constructor.desc.module.anomaly',
    layer: 2,
    authority: Authorities.AiAgentAnomalyRun,
    defaultTrigger: 'scheduled',
    defaultTools: ['book.retrieve', 'portfolio.analytics'],
    defaultMemoryRead: ['semantic'],
    defaultMemoryWrite: [],
    defaultModelClass: 'cheap',
    defaultBudgetPreset: 'standard',
    defaultAutonomyLevel: 'draft-only',
    defaultStakes: 'high',
    defaultClientFacing: false,
    defaultCrossModel: true,
    defaultMission:
      'Scan positions and transactions for unusual movements and draft anomaly flags for human review.',
  },
  // NOTE (D7c): the 'documents' (Document-AI) preset is intentionally REMOVED.
  // Document AI is EXCLUDED from the runtime — its only tool (document.extract)
  // is unwired (KNOWN_TOOLS excludes it; it stays `comingSoon` in TOOL_OPTIONS
  // below), so a 'documents' preset seeded an agent no runtime executor backs.
  // Re-add it ONLY when document.extract is wired end-to-end in the runtime.
  {
    value: 'analytics',
    labelKey: 'pages.copilot.constructor.module.analytics',
    descriptionKey: 'pages.copilot.constructor.desc.module.analytics',
    layer: 4,
    authority: Authorities.AiAgentNlAnalytics,
    defaultTrigger: 'on-demand',
    defaultTools: ['book.retrieve', 'portfolio.analytics'],
    defaultMemoryRead: ['semantic'],
    defaultMemoryWrite: [],
    defaultModelClass: 'strong',
    defaultBudgetPreset: 'standard',
    defaultAutonomyLevel: 'draft-only',
    defaultStakes: 'low',
    defaultClientFacing: false,
    defaultCrossModel: false,
    defaultMission:
      'Answer portfolio analytics questions with grounded, cited figures from the reconciled book.',
  },
  {
    value: 'reporting',
    labelKey: 'pages.copilot.constructor.module.reporting',
    descriptionKey: 'pages.copilot.constructor.desc.module.reporting',
    layer: 5,
    authority: Authorities.AiAgentReportNarration,
    defaultTrigger: 'on-demand',
    defaultTools: ['book.retrieve', 'portfolio.analytics', 'report.draft'],
    defaultMemoryRead: ['semantic'],
    defaultMemoryWrite: [],
    defaultModelClass: 'strong',
    defaultBudgetPreset: 'extended',
    defaultAutonomyLevel: 'draft-only',
    defaultStakes: 'high',
    defaultClientFacing: true,
    defaultCrossModel: true,
    defaultMission:
      'Draft client-facing report narrative from grounded portfolio data for human approval.',
  },
  {
    value: 'mandate',
    labelKey: 'pages.copilot.constructor.module.mandate',
    descriptionKey: 'pages.copilot.constructor.desc.module.mandate',
    layer: 6,
    authority: Authorities.AiAgentMandateRun,
    defaultTrigger: 'scheduled',
    defaultTools: ['book.retrieve', 'mandate.check'],
    defaultMemoryRead: ['semantic'],
    defaultMemoryWrite: [],
    defaultModelClass: 'cheap',
    defaultBudgetPreset: 'standard',
    defaultAutonomyLevel: 'auto-with-review',
    defaultStakes: 'high',
    defaultClientFacing: false,
    defaultCrossModel: true,
    defaultMission:
      'Check portfolios against mandate, suitability, and restriction rules and report violations.',
  },
  {
    // Transaction-compliance (doc 09 §13). Defaults mirror the wired runtime
    // contract in reluna-agent-runtime/src/agents/transaction-compliance/agent.ts
    // (layer 1, event trigger, both memory layers, cheap model tier, standard
    // budget, auto-with-review, high stakes, no cross-model — v1 leans on the
    // deterministic txn-invariants verifier + human gate).
    value: 'txn-compliance',
    labelKey: 'pages.copilot.constructor.module.txn-compliance',
    descriptionKey: 'pages.copilot.constructor.desc.module.txn-compliance',
    layer: 1,
    authority: Authorities.AiAgentTxnComplianceRun,
    defaultTrigger: 'event',
    defaultTools: ['book.retrieve', 'rulebook.retrieve', 'txn.invariants'],
    defaultMemoryRead: [...DEFAULT_MEMORY_READ],
    defaultMemoryWrite: [...DEFAULT_MEMORY_WRITE],
    defaultModelClass: 'cheap',
    defaultBudgetPreset: 'standard',
    defaultAutonomyLevel: 'auto-with-review',
    defaultStakes: 'high',
    defaultClientFacing: false,
    defaultCrossModel: false,
    defaultMission:
      'Validate each transaction against the compliance rulebook and flag violations with the cited rule.',
  },
  {
    // Custom mission-driven agent (phase 3 generic agent platform): authored
    // through POST /agents/create, NOT a wired catalog module. The backend
    // forces draft-only autonomy + deterministic/human verification and allows
    // only on-demand|scheduled triggers, so the preset starts from the safest
    // defaults (guarded budget, low stakes, no memory, no cross-model). The
    // mission is intentionally empty: the author must write their own.
    value: 'custom',
    labelKey: 'pages.copilot.constructor.module.custom',
    descriptionKey: 'pages.copilot.constructor.desc.module.custom',
    layer: 4,
    authority: Authorities.AiAgentNlAnalytics,
    defaultTrigger: 'on-demand',
    defaultTools: ['book.retrieve'],
    defaultMemoryRead: [],
    defaultMemoryWrite: [],
    defaultModelClass: 'cheap',
    defaultBudgetPreset: 'guarded',
    defaultAutonomyLevel: 'draft-only',
    defaultStakes: 'low',
    defaultClientFacing: false,
    defaultCrossModel: false,
    defaultMission: '',
  },
];

/** Allowed authority choices for an agent contract; view/admin rights stay outside agent identity. */
export const AGENT_AUTHORITY_OPTIONS: readonly DescribedContractFormOption[] = [
  {
    value: Authorities.AiAgentReconRun,
    labelKey: 'pages.copilot.constructor.authority.reconRun',
    descriptionKey: 'pages.copilot.constructor.desc.authority.reconRun',
  },
  {
    value: Authorities.AiAgentAnomalyRun,
    labelKey: 'pages.copilot.constructor.authority.anomalyRun',
    descriptionKey: 'pages.copilot.constructor.desc.authority.anomalyRun',
  },
  {
    value: Authorities.AiAgentDocextractRun,
    labelKey: 'pages.copilot.constructor.authority.docextractRun',
    descriptionKey: 'pages.copilot.constructor.desc.authority.docextractRun',
  },
  {
    value: Authorities.AiAgentNlAnalytics,
    labelKey: 'pages.copilot.constructor.authority.nlAnalytics',
    descriptionKey: 'pages.copilot.constructor.desc.authority.nlAnalytics',
  },
  {
    value: Authorities.AiAgentReportNarration,
    labelKey: 'pages.copilot.constructor.authority.reportNarration',
    descriptionKey: 'pages.copilot.constructor.desc.authority.reportNarration',
  },
  {
    value: Authorities.AiAgentMandateRun,
    labelKey: 'pages.copilot.constructor.authority.mandateRun',
    descriptionKey: 'pages.copilot.constructor.desc.authority.mandateRun',
  },
  {
    value: Authorities.AiAgentTxnComplianceRun,
    labelKey: 'pages.copilot.constructor.authority.txnComplianceRun',
    descriptionKey: 'pages.copilot.constructor.desc.authority.txnComplianceRun',
  },
];

/**
 * Static fallback of approve-authorities offered as reviewers (Gate 3 §2.3) when
 * the control-plane catalog is unavailable. The catalog is the PRIMARY source
 * (its approve-authorities drive the multiselect); this list only keeps the
 * no-catalog path non-empty so it can still offer the default
 * `AI_AGENT_RECON_APPROVE` reviewer. Mirrors the backend approve set — the
 * `AI_AGENT_*_APPROVE` authorities from the Authorities constants; RECON first so
 * the seeded default is always a valid option.
 *
 * E8: this IS the Approvals tab's entry-authority set, so it re-exports
 * COPILOT_APPROVE_AUTHORITIES instead of restating it (same members, same order).
 */
export const REVIEWER_APPROVE_AUTHORITIES: readonly string[] = COPILOT_APPROVE_AUTHORITIES;

/**
 * Tools the user can select without typing raw comma-separated ids.
 *
 * IMPORTANT: `contract.tools[]` is validated against the backend KNOWN_TOOLS
 * allowlist (reluna-agent-runtime/src/shared/tools/known-tools.ts). Since the
 * CIO wave that allowlist is the FULL catalog `['book.retrieve', 'book.query',
 * 'txn.invariants', 'custodian.retrieve', 'book.match', 'portfolio.analytics',
 * 'report.draft', 'rulebook.retrieve', 'mandate.check', 'recommendation.query',
 * 'memory.recall', 'news.search', 'market.quotes', 'strategy.retrieve']` —
 * POST /agents/create returns 400 ("tools must be a subset of KNOWN_TOOLS")
 * for any other id. E8f: Document AI is EXCLUDED from the product, so its only
 * tool `document.extract` is no longer offered here at all (it was previously
 * shown `comingSoon`). Add a NEW tool option ONLY when it is added to KNOWN_TOOLS
 * together with a real gateway executor route.
 *
 * Since ADR-0016 the backend ALSO rejects a draft whose tools lack a
 * non-'refuse' dataSourceGrant for each source the tool reads — see
 * TOOL_REQUIRED_SOURCES below (the dialog auto-seeds and validates grants).
 * 'memory.recall' is the one tool gated by the contract's memoryScope.read
 * instead of dataSourceGrants (memory layers are not grounding sources) — see
 * MEMORY_RECALL_TOOL / DURABLE_MEMORY_SCOPE_PRESETS below.
 */
export const TOOL_OPTIONS: readonly DescribedContractFormOption[] = [
  {
    value: 'book.retrieve',
    labelKey: 'pages.copilot.constructor.tool.bookRetrieve',
    descriptionKey: 'pages.copilot.constructor.desc.tool.bookRetrieve',
  },
  {
    value: 'book.query',
    labelKey: 'pages.copilot.constructor.tool.bookQuery',
    descriptionKey: 'pages.copilot.constructor.desc.tool.bookQuery',
  },
  {
    value: 'custodian.retrieve',
    labelKey: 'pages.copilot.constructor.tool.custodianRetrieve',
    descriptionKey: 'pages.copilot.constructor.desc.tool.custodianRetrieve',
  },
  {
    value: 'book.match',
    labelKey: 'pages.copilot.constructor.tool.bookMatch',
    descriptionKey: 'pages.copilot.constructor.desc.tool.bookMatch',
  },
  {
    value: 'portfolio.analytics',
    labelKey: 'pages.copilot.constructor.tool.portfolioAnalytics',
    descriptionKey: 'pages.copilot.constructor.desc.tool.portfolioAnalytics',
  },
  {
    value: 'report.draft',
    labelKey: 'pages.copilot.constructor.tool.reportDraft',
    descriptionKey: 'pages.copilot.constructor.desc.tool.reportDraft',
  },
  {
    value: 'mandate.check',
    labelKey: 'pages.copilot.constructor.tool.mandateCheck',
    descriptionKey: 'pages.copilot.constructor.desc.tool.mandateCheck',
  },
  {
    value: 'rulebook.retrieve',
    labelKey: 'pages.copilot.constructor.tool.rulebookRetrieve',
    descriptionKey: 'pages.copilot.constructor.desc.tool.rulebookRetrieve',
  },
  {
    value: 'txn.invariants',
    labelKey: 'pages.copilot.constructor.tool.txnInvariants',
    descriptionKey: 'pages.copilot.constructor.desc.tool.txnInvariants',
  },
  {
    value: 'recommendation.query',
    labelKey: 'pages.copilot.constructor.tool.recommendationQuery',
    descriptionKey: 'pages.copilot.constructor.desc.tool.recommendationQuery',
  },
  {
    value: 'memory.recall',
    labelKey: 'pages.copilot.constructor.tool.memoryRecall',
    descriptionKey: 'pages.copilot.constructor.desc.tool.memoryRecall',
  },
  {
    value: 'news.search',
    labelKey: 'pages.copilot.constructor.tool.newsSearch',
    descriptionKey: 'pages.copilot.constructor.desc.tool.newsSearch',
  },
  {
    value: 'market.quotes',
    labelKey: 'pages.copilot.constructor.tool.marketQuotes',
    descriptionKey: 'pages.copilot.constructor.desc.tool.marketQuotes',
  },
  {
    value: 'strategy.retrieve',
    labelKey: 'pages.copilot.constructor.tool.strategyRetrieve',
    descriptionKey: 'pages.copilot.constructor.desc.tool.strategyRetrieve',
  },
];

/**
 * The tool ids that are actually selectable/creatable today — the TOOL_OPTIONS
 * entries NOT flagged `comingSoon`, i.e. the subset the backend KNOWN_TOOLS
 * allowlist accepts in `contract.tools[]`. Since E8f (Document AI excluded)
 * TOOL_OPTIONS carries no unwired entry, so this is currently the whole catalog;
 * the `comingSoon` filter is kept so a future unwired option is auto-excluded.
 * Create mode uses this to strip module-preset default tools that would 400 POST
 * /agents/create; edit mode leaves a wired agent's own tool list untouched.
 */
export const SELECTABLE_TOOL_VALUES: readonly string[] = TOOL_OPTIONS.filter(
  (option) => !option.comingSoon,
).map((option) => option.value);

/**
 * Act-Mode WRITE tools the constructor can grant on a custom ACT agent (night
 * build). Each id is BOTH the tool id put in `contract.tools[]` AND the action
 * on the matching writeActionGrant (reluna-agent-runtime KNOWN_WRITE_TOOLS ===
 * WRITE_ACTIONS). The create validator requires a grant for every write tool in
 * `tools[]` (C1: agent proposes, human approves). Only the PHASE_1_WRITE_ACTIONS
 * are exposed as selectable today (they have a real confirm/execute route); the
 * remaining actions stay `comingSoon` — visible but not selectable — so the
 * create can never 400 on an unwired write tool.
 */
export const WRITE_TOOL_OPTIONS: readonly DescribedContractFormOption<WriteActionDTO>[] = [
  {
    value: 'order.propose',
    labelKey: 'pages.copilot.constructor.writeTool.orderPropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.orderPropose',
  },
  {
    value: 'order.postAllocation.propose',
    labelKey: 'pages.copilot.constructor.writeTool.orderPostAllocationPropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.orderPostAllocationPropose',
  },
  {
    value: 'txn.propose',
    labelKey: 'pages.copilot.constructor.writeTool.txnPropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.txnPropose',
  },
  {
    value: 'txn.confirm.propose',
    labelKey: 'pages.copilot.constructor.writeTool.txnConfirmPropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.txnConfirmPropose',
  },
  {
    value: 'client.create.propose',
    labelKey: 'pages.copilot.constructor.writeTool.clientCreatePropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.clientCreatePropose',
    comingSoon: true,
  },
  {
    value: 'account.create.propose',
    labelKey: 'pages.copilot.constructor.writeTool.accountCreatePropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.accountCreatePropose',
    comingSoon: true,
  },
  {
    value: 'mandate.create.propose',
    labelKey: 'pages.copilot.constructor.writeTool.mandateCreatePropose',
    descriptionKey: 'pages.copilot.constructor.desc.writeTool.mandateCreatePropose',
    comingSoon: true,
  },
];

/** The write actions actually selectable today (WRITE_TOOL_OPTIONS not comingSoon). */
export const SELECTABLE_WRITE_ACTIONS: readonly WriteActionDTO[] = WRITE_TOOL_OPTIONS.filter(
  (option) => !option.comingSoon,
).map((option) => option.value);

/** Every write action id (for classifying a tool as a write tool). */
export const WRITE_ACTION_VALUES: readonly WriteActionDTO[] = WRITE_TOOL_OPTIONS.map(
  (option) => option.value,
);

/** True when a tool id is an Act-Mode write action (mirrors backend isWriteAction). */
export function isWriteAction(tool: string): tool is WriteActionDTO {
  return (WRITE_ACTION_VALUES as readonly string[]).includes(tool);
}

/**
 * The data sources each selectable tool ALWAYS reads — MUST mirror the backend
 * TOOL_CATALOG `reads` in reluna-agent-runtime/src/shared/tools/known-tools.ts
 * (the create-time validator in .../src/domain/contract-draft.ts rejects a
 * draft whose tools lack a non-'refuse' grant per required source, and the
 * tool gateway denies the dispatch, ADR-0016).
 */
export const TOOL_REQUIRED_SOURCES: Readonly<Record<string, readonly string[]>> = {
  'book.retrieve': [],
  'book.query': [],
  'custodian.retrieve': ['transactions'],
  'book.match': ['transactions'],
  'portfolio.analytics': ['positions', 'transactions'],
  'report.draft': ['positions', 'transactions'],
  'txn.invariants': ['transactions'],
  'rulebook.retrieve': ['rulebook'],
  'mandate.check': ['mandates'],
  'recommendation.query': ['recommendations'],
  // memory.recall reads NO grant source — memory layers are not grounding
  // sources; its gate is the contract memoryScope.read (see
  // MEMORY_RECALL_TOOL / DURABLE_MEMORY_SCOPE_PRESETS below).
  'memory.recall': [],
  'news.search': ['news'],
  'market.quotes': ['prices'],
  'strategy.retrieve': ['strategies'],
};

/**
 * The one tool gated by memoryScope instead of dataSourceGrants (backend
 * known-tools.ts): 'memory.recall' serves only the DURABLE layers, and the
 * create validator rejects a draft granting the tool without a readable
 * episodic/procedural layer ("every dispatch would be denied by the gateway
 * clamp"). The dialog mirrors that rule client-side.
 */
export const MEMORY_RECALL_TOOL = 'memory.recall';

/**
 * The DURABLE memory layers 'memory.recall' actually serves. The create/update
 * validator rejects a draft that grants the tool while `memoryScope.read`
 * includes neither ("every dispatch would be denied by the gateway clamp"), so
 * the constructor mirrors the rule against these exact layer names.
 */
export const DURABLE_MEMORY_LAYERS: readonly string[] = ['episodic', 'procedural'];

/**
 * Kind-routing tools (backend `readsByInputKind`): create-time the backend
 * demands a non-'refuse' grant for AT LEAST ONE of the listed sources (the
 * dispatch is then gated by the actual input kind). Mirrors the backend
 * TOOL_CATALOG exactly like TOOL_REQUIRED_SOURCES.
 */
export const TOOL_ANY_OF_SOURCES: Readonly<Record<string, readonly string[]>> = {
  'book.retrieve': ['transactions', 'positions'],
  'book.query': ['transactions', 'positions'],
};

/**
 * De-duplicated union of the sources the given tools may need — strict
 * requirements plus every any-of member (seeding BOTH kinds for book.retrieve
 * and book.query is a safe superset that can never 400) — in
 * DATA_SOURCE_OPTIONS order (stable UI order). Unknown/coming-soon tool ids
 * contribute nothing (the backend rejects them on the KNOWN_TOOLS check first).
 */
export function requiredSourcesForTools(tools: readonly string[]): string[] {
  const required = new Set<string>();
  for (const tool of tools) {
    for (const source of TOOL_REQUIRED_SOURCES[tool] ?? []) {
      required.add(source);
    }
    for (const source of TOOL_ANY_OF_SOURCES[tool] ?? []) {
      required.add(source);
    }
  }
  return DATA_SOURCE_OPTIONS.map((option) => option.value).filter((value) => required.has(value));
}

/** User-facing budget presets; the hard caps remain visible and editable. */
export const BUDGET_PRESET_OPTIONS: readonly BudgetPresetOption[] = [
  {
    value: 'guarded',
    labelKey: 'pages.copilot.constructor.budgetPreset.guarded',
    descriptionKey: 'pages.copilot.constructor.desc.budgetPreset.guarded',
    budget: { maxSteps: 8, maxTokens: 10000, maxWallClockMs: 30000 },
  },
  {
    value: 'standard',
    labelKey: 'pages.copilot.constructor.budgetPreset.standard',
    descriptionKey: 'pages.copilot.constructor.desc.budgetPreset.standard',
    budget: { maxSteps: 25, maxTokens: 200000, maxWallClockMs: 60000 },
  },
  {
    value: 'extended',
    labelKey: 'pages.copilot.constructor.budgetPreset.extended',
    descriptionKey: 'pages.copilot.constructor.desc.budgetPreset.extended',
    budget: { maxSteps: 60, maxTokens: 500000, maxWallClockMs: 180000 },
  },
];

// --- AIC debate (ADR-0005) constructor options (CIO wave 2) -------------------

/**
 * AI vendors a modelRoles binding may name (backend ModelRoleRef.vendor). The
 * runtime validates the vendor against its CONFIGURED roster at admission —
 * a vendor without a key in System Settings → Integrations is a 400 with the
 * configured list in the message (surfaced verbatim in the dialog), so this
 * list is the UNIVERSE of supported lanes, not a promise they are configured.
 */
export const DEBATE_VENDOR_OPTIONS: readonly ContractFormOption[] = [
  { value: 'anthropic', labelKey: 'pages.copilot.constructor.debate.vendor.anthropic' },
  { value: 'openai', labelKey: 'pages.copilot.constructor.debate.vendor.openai' },
  { value: 'gemini', labelKey: 'pages.copilot.constructor.debate.vendor.gemini' },
];

/**
 * Optional per-role model tier ('' = resolve through the vendor's defaults;
 * backend: modelClass ignored when a concrete modelId is set).
 */
export const DEBATE_MODEL_CLASS_OPTIONS: readonly ContractFormOption[] = [
  { value: '', labelKey: 'pages.copilot.constructor.debate.modelClass.default' },
  { value: 'cheap', labelKey: 'pages.copilot.constructor.modelClass.cheap' },
  { value: 'strong', labelKey: 'pages.copilot.constructor.modelClass.strong' },
];

/** Panelist bounds POST /agents/create enforces (backend contract-draft.ts). */
export const DEBATE_PANELISTS_MIN = 1;
export const DEBATE_PANELISTS_MAX = 5;

/** Bound for a modelRoles binding's concrete model id (backend MODEL_ID_MAX_CHARS). */
export const DEBATE_MODEL_ID_MAX_LENGTH = 128;

/**
 * Cross-model verifier tier options — the REAL runtime model classes POST
 * /agents/create + /agents/validate accept for `verifierModelClass`
 * ('cheap' | 'strong'; contract-draft.ts CONTRACT_FIELD_CATALOG.verifierModelClass
 * enum). The old 'local' / 'frontier' options were a 400 on the wire (they are
 * not model classes) AND a false-privacy affordance, so they are removed (Gate 3
 * §2.3 "trim FE VERIFIER_MODEL_OPTIONS local/frontier that 400"). The verifier
 * lane runs on an INDEPENDENT configured vendor; the dialog only offers cross-model
 * when a second vendor is configured (catalog.models.vendors.length >= 2).
 */
export const VERIFIER_MODEL_OPTIONS: readonly DescribedContractFormOption<ModelClassDTO>[] = [
  {
    value: 'cheap',
    labelKey: 'pages.copilot.constructor.modelClass.cheap',
    descriptionKey: 'pages.copilot.constructor.desc.verifier.cheap',
  },
  {
    value: 'strong',
    labelKey: 'pages.copilot.constructor.verifier.strong',
    descriptionKey: 'pages.copilot.constructor.desc.verifier.strong',
  },
];
